import { ApBillStatus } from '../generated/enums.js';

/** Payable AP bill statuses that can transition to PARTIALLY_PAID or PAID */
const PAYABLE_AP_BILL_STATUSES: ApBillStatus[] = [
  ApBillStatus.APPROVED,
  ApBillStatus.PARTIALLY_PAID,
];

/**
 * True for a bill migrated from the customer's previous system at onboarding. Such a bill is
 * informational: it never posts to GL, is never a reconciliation candidate, is never projected
 * in cash flow, and is hidden from every list unless the caller asks for it by status. It has
 * no transition in or out in v1 (an "activate" action is a follow-up).
 */
export function isMigratedApBillStatus(status: ApBillStatus | string | null | undefined): boolean {
  return status === ApBillStatus.MIGRATED;
}

/**
 * AP bill statuses that never take part in a recurring template: they cannot be linked to
 * one, and they are invisible to the recurring projection, the template timeline and the
 * recurring dashboard. VOID is a cancelled occurrence. MIGRATED is history from the previous
 * system whose amount already lives in the opening balance, so counting it as a received or
 * future occurrence would double the obligation.
 *
 * Every recurring link/update/projection/timeline query filters with this one set so the
 * rules cannot drift apart. Use it as `billStatus: { notIn: [...] }` in Prisma, or through
 * {@link isApBillExcludedFromRecurring} on rows already loaded.
 */
export const AP_BILL_STATUSES_EXCLUDED_FROM_RECURRING: readonly ApBillStatus[] = [
  ApBillStatus.VOID,
  ApBillStatus.MIGRATED,
];

const AP_BILL_STATUSES_EXCLUDED_FROM_RECURRING_SET: ReadonlySet<string> = new Set(
  AP_BILL_STATUSES_EXCLUDED_FROM_RECURRING,
);

/** True when the bill must be ignored by every recurring-template flow. */
export function isApBillExcludedFromRecurring(
  status: ApBillStatus | string | null | undefined,
): boolean {
  return status != null && AP_BILL_STATUSES_EXCLUDED_FROM_RECURRING_SET.has(status);
}

/**
 * Calculate AP bill status based on allocated amount vs bill total.
 * Pure function - no side effects.
 */
export function calculateApBillStatus(
  currentStatus: ApBillStatus,
  totalAllocated: number,
  billTotal: number,
  billType?: string | null,
): ApBillStatus {
  if (
    currentStatus === ApBillStatus.DRAFT &&
    (billType === 'BANK_FEE' || billType === 'VENDOR_ADVANCEMENT') &&
    billTotal > 0 &&
    totalAllocated >= billTotal
  ) {
    return ApBillStatus.COMPLETED;
  }

  if (
    currentStatus === ApBillStatus.DRAFT ||
    currentStatus === ApBillStatus.SUBMITTED ||
    currentStatus === ApBillStatus.PENDING_APPROVAL ||
    currentStatus === ApBillStatus.REJECTED ||
    currentStatus === ApBillStatus.COMPLETED ||
    currentStatus === ApBillStatus.VOID ||
    // Migrated bills carry paid/open in isFullyPaid/totalPaidAmount, never in the status.
    currentStatus === ApBillStatus.MIGRATED
  ) {
    return currentStatus;
  }

  if (!PAYABLE_AP_BILL_STATUSES.includes(currentStatus)) {
    return currentStatus;
  }

  if (currentStatus === ApBillStatus.APPROVED) {
    if (totalAllocated >= billTotal) return ApBillStatus.PAID;
    if (totalAllocated > 0) return ApBillStatus.PARTIALLY_PAID;
    return currentStatus;
  }

  if (totalAllocated >= billTotal) return ApBillStatus.PAID;
  if (totalAllocated > 0) return ApBillStatus.PARTIALLY_PAID;
  return currentStatus;
}

/** Recalculate payment-derived states after a cancellation or corrective allocation change. */
export function recalculateApBillPaymentStatus(
  currentStatus: ApBillStatus,
  totalPaidAmount: number,
  billTotal: number,
): ApBillStatus {
  if (currentStatus !== ApBillStatus.PAID && currentStatus !== ApBillStatus.PARTIALLY_PAID) {
    return currentStatus;
  }
  if (billTotal > 0 && totalPaidAmount >= billTotal) return ApBillStatus.PAID;
  if (totalPaidAmount > 0) return ApBillStatus.PARTIALLY_PAID;
  return ApBillStatus.APPROVED;
}
